import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Input } from './ui';

/**
 * A password box with a "show" toggle.
 *
 * Typing a twelve-character passphrase blind on a phone keyboard is how
 * people end up locked out by their own typo; letting them look is the
 * cheapest fix there is. The toggle is a real button with a name that says
 * what it will do, and it is not a submit button, so pressing Enter in the
 * field still signs in.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className, ...props }, ref) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        // A password is not prose and has no direction to guess; a field that
        // flips to right-to-left on the first Arabic character would scramble
        // where the caret appears while somebody types blind.
        dir="ltr"
        {...props}
        type={visible ? 'text' : 'password'}
        // Browsers would otherwise offer to correct and capitalise a password
        // the moment it became visible.
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        className={cn('pe-9', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 end-0 flex w-8 items-center justify-center rounded-e-control text-ink-subtle transition-colors hover:text-ink"
      >
        {visible ? <EyeOff aria-hidden className="size-4" /> : <Eye aria-hidden className="size-4" />}
      </button>
    </div>
  );
});
